import { SearchProduct } from "@nosto/nosto-js/client"

type WithHandle = SearchProduct & { handle?: string }

export function handleDecorator(product: SearchProduct): WithHandle {
  if (!product.url) {
    return product
  }

  try {
    // Nosto often returns root-relative `/products/...` URLs. `new URL('/path')` throws
    // without a base, which would drop `handle` and cause grid cards to render nothing.
    const origin =
      typeof window !== "undefined" && typeof window.location?.origin === "string"
        ? window.location.origin
        : "https://shopify.com"
    const pathname = new URL(product.url, origin).pathname
    const trimmed = pathname.replace(/\/+$/, "")
    const segments = trimmed.split("/").filter(Boolean)
    const handle = segments.length > 0 ? segments[segments.length - 1] : undefined

    return {
      ...product,
      handle: handle || undefined
    }
  } catch {
    return product
  }
}

/**
 * Custom-field key that Nosto must index for the Shopify style metaobject ID
 * (`product.metafields.custom.style.value.id`) per Agent A's data contract
 * (`docs/agent-a-nosto-data-contract.md` §1).
 *
 * Sibling products that share a Shopify style metaobject share this value, so
 * grouping by it gives one card per style family in the Nosto search response.
 */
export const STYLE_FAMILY_CUSTOM_FIELD_KEY = "styleFamilyId"

/**
 * Live Dickies Nosto indexing exposes the Shopify style metaobject reference on this custom field
 * when `styleFamilyId` is not present (see `docs/live-data-validation/findings.md`).
 */
export const STYLE_FAMILY_CUSTOM_STYLE_KEY = "custom-style"

const STYLE_FAMILY_SOURCE_KEYS = [STYLE_FAMILY_CUSTOM_FIELD_KEY, STYLE_FAMILY_CUSTOM_STYLE_KEY] as const

type WithStyleFamily = SearchProduct & { styleFamilyKey?: string }

/**
 * Normalize the Nosto-indexed style-family value into a stable `styleFamilyKey`
 * so downstream code (dedupe in `dedupeByStyleFamily`, grid rendering) does not
 * have to reach into `customFields` itself or care about indexer naming.
 *
 * Behaviour:
 * - Prefer `styleFamilyId` when indexed (Agent A contract).
 * - Fall back to `custom-style`, which currently carries the Shopify style metaobject GID on Dickies.
 * - Treats empty / whitespace-only values as missing so the dedupe helper
 *   skips grouping for products without a real family key (matches plan
 *   "If no confirmed key exists for a hit, do not dedupe that hit.").
 */
export function styleFamilyDecorator(product: SearchProduct): WithStyleFamily {
  for (const sourceKey of STYLE_FAMILY_SOURCE_KEYS) {
    const raw = product.customFields?.find(field => field.key === sourceKey)?.value
    const normalized = typeof raw === "string" ? raw.trim() : ""
    const styleFamilyKey = normalized.length > 0 ? normalized : undefined

    if (styleFamilyKey !== undefined) {
      return {
        ...product,
        styleFamilyKey
      }
    }
  }

  return product
}
